// it is possible much of this could be re-used for users
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import mergeWith from 'lodash/mergeWith';
import unset from 'lodash/unset';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { DETAIL_MODES } from '../../form/constants';
import { STAGING_OVERRIDE_PATH, DELETION_MARK } from './constants';
import { FilterManager } from '../../table/filter-manager';
import { flattenedKeys } from '../../util/object-helpers';
import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING } from '../../util/constants';
import {
    calcDefaultValue,
    isItemDirty,
    isGroupDirty,
    generateDefaultData,
} from '../../util/data-helpers';
import { buildHistoryRecords } from '../../util/history-helpers';

const _siteSportSelector = (state) => state.site.sport;
export const _availableLeaguesSelector = (state) => state.site.leagueData || EMPTY_ARRAY;
export const siteIsLoadingSelector = (state) => state.site.isLoading || state.site.isLoadingSite;
export const siteSeasondDataIsLoadingSelector = (state) =>
    state.site.selectedSiteLeagueData?.isLoading;

export const siteIsSavingSelector = (state) => state.site.isSaving;
// note name is used like id for site
export const selectedSiteNamesSelector = (state) => state.site.selectedSiteNames || EMPTY_ARRAY;
export const siteConfigTemplatesSelector = ({ site }) => site.templates || EMPTY_OBJECT;
const _stagedEditsSelector = (state) => state.site.stagedEdits || EMPTY_OBJECT;
export const selectedParentSiteSelector = (state) => state.site.parentSite || EMPTY_STRING;

export const siteSportSelector = createSelector(
    _siteSportSelector,
    siteConfigTemplatesSelector,
    (sport, templates) => {
        // if we have one, we're good to go
        if (sport) {
            return sport;
        }
        if (templates !== EMPTY_OBJECT && !isEmpty(templates)) {
            return Object.keys(templates)[0];
        }
    }
);

export const siteSportOptionsSelector = createSelector(siteConfigTemplatesSelector, (templates) =>
    Object.keys(templates).map((sport) => ({ name: sport, displayKey: sport }))
);

// todo could we base this on selected site to allow for cross sport editing? (i.e. espn/other media child sites)
export const siteConfigTemplateSelector = createSelector(
    siteConfigTemplatesSelector,
    siteSportSelector,
    (templates, templateType) => {
        const template = templates[templateType];
        if (!template) {
            return EMPTY_OBJECT;
        }
        // clone required b/c we may need to modify for auths
        const clonedTemplate = cloneDeep(template);
        return clonedTemplate;
    }
);

// todo here we need to get the template and figure out the defaults, maybe just if null?
//export const siteStagedEditsSelector = (state) => state.site.stagedEdits || {};
export const siteValidatedPathsSelector = (state) => state.site.validatedPaths || EMPTY_ARRAY;
export const siteValidationErrorsSelector = (state) => state.site.validationErrors || EMPTY_OBJECT;
export const siteDetailModeSelector = (state) => state.site.detailMode || DETAIL_MODES.VIEW;
export const sitePassedValidationSelectorFactory = (path) =>
    createSelector(siteValidatedPathsSelector, (validatedPaths) => {
        return validatedPaths.includes(path);
    });

const _siteListSelector = (state) => state.site.sites || EMPTY_ARRAY;
export const isLoadingSiteListSelector = (state) => state.site.isLoadingSites;
export const isLoadingLeagueDataSelector = (state) => state.site.isLoadingLeagueData;
export const siteTemplateListIsLoadingSelector = (state) => state.site.templateListIsLoading;
export const tokenListIsLoadingSelector = (state) => state.site.isLoadingTokens;

export const siteTokensSelector = (state) => state.site.tokens || EMPTY_ARRAY;
export const siteHistorySelector = (state) => state.site.siteHistory || EMPTY_ARRAY;
export const isLoadingSiteHistorySelector = (state) => !!state.site.isLoadingSiteHistory;

export const isSiteAdminReadySelector = createSelector(
    isLoadingSiteListSelector,
    isLoadingLeagueDataSelector,
    siteTemplateListIsLoadingSelector,
    (siteListLoading, isLoadingLeagueData, siteTemplatesLoading) =>
        !siteListLoading && !siteTemplatesLoading && !isLoadingLeagueData
);

export const siteListTableSelector = createSelector(
    _siteListSelector,
    //(state) => state.site.selectedSites || [],
    (sites) => {
        return sites.map((site) => {
            // kind of a hack but allows sorting to group in a sensible way
            let active = site.active;
            if (site.active && site.authorizations?.softDeactivation) {
                active = -1;
            }
            return {
                name: site.core?.name,
                domain: site.core?.domain,
                sport: site.sport,
                active: active,
                updated: site?.meta?.updated,
                guid: site.guid,
                // emailAccountDomain: site.core?.security?.authentication?.emailAccountDomain,
                // authSiteName: site.core?.security?.authentication?.authContext?.authSiteName,
                emailAccountDomain: site.meta.emailAccountDomain,
                authSiteName: site.meta.authSiteName,
                canAcceptChildSites: site.meta.canAcceptChildSites,
                softDeactivation: site.authorizations?.softDeactivation,
            };
        });
    }
);

export const selectedSiteDataSelector = createSelector(
    _siteListSelector,
    selectedSiteNamesSelector,
    (sites, selectedSiteNames) => {
        // first for now, eventually we can do something better
        if (!selectedSiteNames?.length) {
            return EMPTY_OBJECT;
        }
        const firstSelectedSite = selectedSiteNames[0];
        const selectedSite = sites.find((u) => u?.core?.name === firstSelectedSite);
        // here we need to only supply template fields?
        return selectedSite || EMPTY_OBJECT;
    }
);

// to be used only if we don't have a currently have a parent selected but we want to allow the user to initiate a parent-based action
export const defaultParentSiteSelector = createSelector(siteListTableSelector, (tableSites) => {
    const parentSiteRec = tableSites.find((s) => s.canAcceptChildSites);
    if (parentSiteRec) {
        const autoSelectedParent = parentSiteRec.authSiteName;
        return autoSelectedParent;
    }
});

// defaults selector based on templates
export const defaultSiteDataSelector = createSelector(siteConfigTemplateSelector, (siteTemplate) =>
    generateDefaultData(siteTemplate)
);
// note defaults used here in selector so we stay current even if sitedata loads before the default data
export const siteStagedEditsSelector = createSelector(
    selectedSiteDataSelector,
    _stagedEditsSelector,
    defaultSiteDataSelector,
    siteDetailModeSelector,
    // todo this fn may be candidate for data-util as well
    (siteData, stagedEdits, defaultData, detailMode) => {
        const isEditOrNewMode = detailMode !== DETAIL_MODES.VIEW;
        if (isEditOrNewMode) {
            const merged = mergeWith({}, defaultData, siteData, stagedEdits, customizer);
            // this removes overrides from the merged data that are marked for deletion.
            updateStagingOverrides(merged.staging.overrides, stagedEdits?.staging?.overrides);
            return merged;
        }
        return siteData;
    }
);

export const selectedSiteGuidSelector = createSelector(
    siteStagedEditsSelector,
    (siteData) => siteData?.guid
);

function updateStagingOverrides(overrides = {}, stagedOverrides) {
    const stagedKeys = flattenedKeys(stagedOverrides);
    for (const stagedKey of stagedKeys) {
        const markedForDeletion = get(stagedOverrides, stagedKey) === DELETION_MARK;
        if (markedForDeletion) {
            unset(overrides, stagedKey);
        }
    }
}

function customizer(objValue, srcValue, key, obj, src, stk) {
    if (isArray(objValue) || isArray(srcValue)) {
        return srcValue;
    }
}

export const siteElementValueFactory = (path, itemTemplate) =>
    createSelector(
        selectedSiteDataSelector,
        siteStagedEditsSelector,
        siteDetailModeSelector,
        (siteData, stagedEdits, detailMode) => {
            const defaultValue = calcDefaultValue(itemTemplate);
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            const sourceData = isViewMode ? siteData : stagedEdits;
            return get(sourceData, path, defaultValue);
        }
    );

// does the current path have an override value?
export const siteElementHasOverrideFactory = (path) =>
    createSelector(
        selectedSiteDataSelector,
        siteStagedEditsSelector,
        siteDetailModeSelector,
        (siteData, stagedEdits, detailMode) => {
            // this approach lets this work for path == original path OR the path == staging.override
            const overRidePath = getOverridePath(path);
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            const sourceData = isViewMode ? siteData : stagedEdits;
            const overrideValue = get(sourceData, overRidePath, undefined);
            return overrideValue !== undefined;
        }
    );

// gets the overridden value if present otherwise the original value
export const siteElementOverrideValueFactory = (path, itemTemplate) =>
    createSelector(
        selectedSiteDataSelector,
        siteStagedEditsSelector,
        siteDetailModeSelector,
        siteElementHasOverrideFactory(path),
        (siteData, stagedEdits, detailMode, hasOverride) => {
            // this approach lets this work for path == original path OR the path == staging.override
            const basePath = getBasePath(path);
            const overRidePath = getOverridePath(path);
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            const sourceData = isViewMode ? siteData : stagedEdits;
            if (hasOverride) {
                return get(sourceData, overRidePath, undefined);
            }
            const defaultValue = calcDefaultValue(itemTemplate);
            return get(sourceData, basePath, defaultValue);
        }
    );

export const siteElementIsReadOnlyFactory = (path, itemTemplate) =>
    createSelector(siteDetailModeSelector, (detailMode) => {
        // this approach lets this work for path == original path OR the path == staging.override
        const isViewMode = detailMode === DETAIL_MODES.VIEW;
        const isEditMode = detailMode === DETAIL_MODES.EDIT;
        // not sure I want the view mode check too but for now we'll do it that way
        return isViewMode || (isEditMode && itemTemplate.notEditable);
    });

// technically not selectors -- these are just convienence fuctions to always get either base or override path given a path that could be either
export const getBasePath = (path) => {
    const basePath = path.replace(STAGING_OVERRIDE_PATH + '.', '');
    return basePath;
};

export const getOverridePath = (path) => {
    const basePath = getBasePath(path);
    return `${STAGING_OVERRIDE_PATH}.${basePath}`;
};

export const siteElementIsDirtyFactory = (path, itemTemplate) =>
    createSelector(
        selectedSiteDataSelector,
        siteStagedEditsSelector,
        siteDetailModeSelector,
        (siteData, stagedEdits, detailMode) => {
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            if (isViewMode) {
                return false;
            }
            return isItemDirty(itemTemplate, path, siteData, stagedEdits);
        }
    );

export const isActiveRecordDirty = createSelector(
    siteConfigTemplateSelector,
    selectedSiteDataSelector,
    siteStagedEditsSelector,
    siteDetailModeSelector,
    (siteTemplate, siteData, stagedEdits, detailMode) => {
        const isViewMode = detailMode === DETAIL_MODES.VIEW;
        if (isViewMode) {
            return false;
        }
        if (isEmpty(stagedEdits)) {
            return false;
        }
        return isGroupDirty(siteTemplate, siteData, stagedEdits);
    }
);
// this is mainly so the deeply nested soccer season auths can be shown/hidden based on selected authorizations
export const activeRecordSiteAuths = createSelector(
    siteElementValueFactory('authorizations', { defaultValue: EMPTY_OBJECT }),
    (value) => value
);

// export const siteElementIsDirtyFactory = (path, itemTemplate) =>
//     createSelector(
//         selectedSiteDataSelector,
//         siteStagedEditsSelector,
//         siteDetailModeSelector,
//         (siteData, stagedEdits, detailMode) => {
//             const isViewMode = detailMode === DETAIL_MODES.VIEW;
//             if (isViewMode) {
//                 return false;
//             }
//             const defaultValue = calcDefaultValue(itemTemplate);
//             const origValue = get(siteData, path, defaultValue);
//             const stagedValue = get(stagedEdits, path, defaultValue);
//             if (itemTemplate.type === 'AUTH_CONTEXT') {
//                 if (isPlainObject(origValue)) {
//                     return !isEqual(origValue, stagedValue);
//                 }
//             }
//             return stagedValue !== origValue;
//         }
//     );

export const siteElementErrorFactory = (path) =>
    createSelector(siteValidationErrorsSelector, (validationErrors) => {
        return get(validationErrors, path);
    });

export const itemTemplateForPathFactory = (path) =>
    createSelector(siteConfigTemplateSelector, (siteTemplate) => {
        if (!path) {
            return;
        }
        // need to a dd a ".properties" for each non-terminal node here
        const pathParts = path.split('.');
        // 'a.b.c' goes to a.properties b.properties .c
        const pathWithProperties = pathParts.join('.properties.');
        return get(siteTemplate, pathWithProperties);
    });

export const siteElementValueByPathFactory = (path) => {
    const itemTemplateSelector = itemTemplateForPathFactory(path);
    return createSelector(
        itemTemplateSelector,
        selectedSiteDataSelector,
        siteStagedEditsSelector,
        siteDetailModeSelector,
        (itemTemplate, siteData, stagedEdits, detailMode) => {
            const defaultValue = calcDefaultValue(itemTemplate);
            const isViewMode = detailMode === DETAIL_MODES.VIEW;
            const sourceData = isViewMode ? siteData : stagedEdits;
            return get(sourceData, path, defaultValue);
        }
    );
};

export const availableLeaguesSelector = createSelector(
    _availableLeaguesSelector,
    _siteSportSelector,
    siteElementValueByPathFactory('leagueData.classAuth'),
    (seasons, sport, classAuth) => {
        const sorter = (a, b) => {
            const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
            const aVal = a['displayKey'];
            const bVal = b['displayKey'];
            return collator.compare(aVal, bVal);
        };
        const filteredSeasons =
            sport === 'rugby'
                ? seasons.filter((league) => league?.classAuth === classAuth)
                : seasons;

        return filteredSeasons.sort(sorter);
    }
);

export const siteHistoryRecordsSelector = createSelector(
    siteHistorySelector,
    siteConfigTemplateSelector,
    (siteHistory, siteTemplate) => buildHistoryRecords(siteHistory, siteTemplate)
);
